import React from 'react';
import { Auth } from 'aws-amplify';

import 'bootstrap/dist/css/bootstrap.min.css';
import { Form, Button, Spinner } from 'react-bootstrap';

import UserContext from './../../app/UserContext';
import { SIGN } from './../../shared/constants/index';

/**
 * サインイン
 */
class SignIn extends React.Component {
  // 画面の状態（state）を定義します。
  state = {
    username: '',
    password: '',
    showConfirmation: false,
    user: {},
    isLoading: false,
  };

  // コンテキスト（親<App>から配られる「現在のログインユーザ」や更新関数）を使います。
  static contextType = UserContext;

  // autofill（自動入力）対策
  usernameRef = React.createRef();
  passwordRef = React.createRef();
  _autofillTimer = null;
  _autofillDeadline = null;

  componentDidMount() {
    // ページ表示直後に、短時間だけ <input> の実値を監視（autofill 対策）
    const syncFromRefs = () => {
      const u = this.usernameRef.current?.value || '';
      const p = this.passwordRef.current?.value || '';

      if (u && p) {
        this.setState(prev => ({
          username: prev.username || u,
          password: prev.password || p,
        }));
        clearInterval(this._autofillTimer);
        this._autofillTimer = null;
      }

      if (Date.now() > this._autofillDeadline) {
        clearInterval(this._autofillTimer);
        this._autofillTimer = null;
      }
    };

    this._autofillDeadline = Date.now() + 3000; // 最大3秒
    this._autofillTimer = setInterval(syncFromRefs, 300);
    syncFromRefs();
  }

  componentWillUnmount() {
    if (this._autofillTimer) clearInterval(this._autofillTimer);
  }

  /**
   * stateの引数keyを引数valueに更新する（入力フォームの onChange から呼ばれます）
   * - エラーメッセージは入力が変わったタイミングで消す
   */
  onChange = (key, value) => {
    this.props.updateError(null);
    this.setState({ [key]: value });
  };

  /**
   * サインイン（フォーム送信時）
   */
  signIn = event => {
    event.preventDefault();
    event.stopPropagation();

    // 読み込み中フラグON
    this.setState({ isLoading: true, showConfirmation: false });
    this.props.updateError(null);

    const form = event.currentTarget;
    if (!form.checkValidity()) {
      form.classList.add('was-validated');
      this.setState({ isLoading: false });
      return;
    }

    const { history } = this.props;
    const { updateCurrentUser } = this.context;

    // 再度 ref から値を取得（autofill が送信時に反映されたケースをカバー）
    const refUsername = this.usernameRef.current?.value || '';
    const refPassword = this.passwordRef.current?.value || '';
    const username = refUsername || this.state.username;
    const password = refPassword || this.state.password;

    // state と実際に使う値を揃えておく
    this.setState({ username, password });

    Auth.signIn(username, password)
      .then(user => {
        if (!user.signInUserSession) {
          // セッションが無ければ汎用エラー扱い
          this.props.updateError('NotAuthorizedException');
          this.setState({
            user,
            showConfirmation: true,
            isLoading: false,
          });
          return;
        }

        updateCurrentUser(user);

        this.setState({ isLoading: false });

        history.push({
          pathname: '/dashboard',
          state: {
            notice: SIGN.IN.SUCCESS,
          },
        });
      })
      .catch(err => {
        this.props.updateError(err.name);
        this.setState({ isLoading: false });
      });
  };

  /**
   * 描画
   */
  render() {
    const { username, password, isLoading, showConfirmation } = this.state;

    // ボタンが押せる条件
    const canSubmit = !!(username && password) && !isLoading;

    return (
      <>
        {
          // サインイン画面
          !showConfirmation && (
            <Form
              noValidate
              className="needs-validation"
              onSubmit={this.signIn}
            >
              <Form.Group className="mb-3" controlId="formUserId">
                <Form.Label>ユーザID</Form.Label>
                <Form.Control
                  required
                  type="text"
                  autoComplete="username"
                  placeholder="ユーザIDを入力"
                  disabled={isLoading}
                  ref={this.usernameRef}
                  onChange={evt =>
                    this.onChange('username', evt.target.value)
                  }
                />
                <Form.Control.Feedback type="invalid">
                  ユーザIDを入力してください
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3" controlId="formPassword">
                <Form.Label>パスワード</Form.Label>
                <Form.Control
                  required
                  type="password"
                  autoComplete="current-password"
                  placeholder="パスワードを入力"
                  disabled={isLoading}
                  ref={this.passwordRef}
                  onChange={evt =>
                    this.onChange('password', evt.target.value)
                  }
                />
                <Form.Control.Feedback type="invalid">
                  パスワードを入力してください
                </Form.Control.Feedback>
              </Form.Group>

              {isLoading ? (
                <Button variant="primary" disabled>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                  />{' '}
                  Loading...
                </Button>
              ) : (
                <Button
                  variant="primary"
                  type="submit"
                  disabled={!canSubmit}
                >
                  サインイン
                </Button>
              )}
            </Form>
          )
        }

        {
          // 認証されていない（セッション無し）と判断した場合の表示
          showConfirmation && (
            <>
              <h3>不正なユーザです</h3>
              <Button
                variant="primary"
                onClick={() => this.setState({ showConfirmation: false })}
              >
                戻る
              </Button>
            </>
          )
        }
      </>
    );
  }
}

export default SignIn;
